import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    LinearProgress,
    TextField,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import { ShoppingCart, ArrowDownward, ArrowUpward } from '@mui/icons-material';
import ActionButton from './ActionButton.jsx';
import ProductGallery from './ProductGallery.jsx';
import PromoCodeBar from './PromoCodeBar.jsx';
import ProductPreviewArea, { resolvePreviewProps } from './ProductPreviewArea.jsx';
import { PRODUCTS } from '../products';
import { PALETTE, alpha } from '../theme';

// Featured row order: cup → t-shirt → glass coaster → stone magnet. Shared by
// all three tools (Trail Maps, Travel Maps, Photo Shaper) so the collapsed
// featured row is consistent everywhere.
const FEATURED_IDS = ['cup', 'tshirt', 'glass-coaster', 'stone-magnet'];

const FEATURED_PRODUCTS = FEATURED_IDS
    .map((id) => PRODUCTS.find((p) => p.id === id))
    .filter((p) => p && !p.hidden);

// The "See all N products" link must match what the expanded gallery actually
// renders — ProductGallery filters out `hidden` products, so count those out
// too (otherwise the label over-promises).
const VISIBLE_PRODUCT_COUNT = PRODUCTS.filter((p) => !p.hidden).length;

/**
 * Single product panel that lives directly under the result. Starts collapsed
 * with three featured products (Cup, T-Shirt, Pillow) so people see purchase
 * as the obvious next step. Clicking "See all products" expands the same
 * orange-tinted card into the full gallery — captions, no-caption checkboxes,
 * promo bar — so there is only ONE product view on the page, never two.
 */
const QuickOrderStrip = ({
    featuredProducts = FEATURED_PRODUCTS,
    defaultLabel = '',
    prepareImage,
    onOrder,
    designSrc,
}) => {
    const [expanded, setExpanded] = useState(false);
    const [activeProductId, setActiveProductId] = useState(null);
    const [progress, setProgress] = useState(0);
    // Per-product "No caption" toggles for the compact featured row (mirrors
    // the expanded ProductGallery). Only meaningful for products with a label.
    const [noLabelFlags, setNoLabelFlags] = useState({});
    const handleNoLabelToggle = (productId, checked) =>
        setNoLabelFlags((prev) => ({ ...prev, [productId]: checked }));
    // `undefined` ⇒ user hasn't touched it → live default flows through.
    // Empty string ⇒ user deliberately cleared it.
    const [captionOverride, setCaptionOverride] = useState(undefined);

    const effectiveCaption =
        captionOverride !== undefined ? captionOverride : (defaultLabel || '');

    const handleOrderClick = useCallback(
        async (product) => {
            if (activeProductId) return;
            const useNoLabel = product.supportsLabel && !!noLabelFlags[product.id];
            // Route to the no-label SKU (+ swap preview image) when available;
            // otherwise the same template handles it via an empty caption.
            const effectiveProduct =
                useNoLabel && product.noLabelProductId
                    ? { ...product, zazzleProductId: product.noLabelProductId, image: product.noLabelImage || product.image, analyticsKey: `${product.analyticsKey}_no_label` }
                    : product;
            const label = useNoLabel || !product.supportsLabel ? '' : effectiveCaption;
            try {
                setActiveProductId(product.id);
                setProgress(0);
                const imageUrl = await prepareImage((pct) => setProgress(pct));
                if (imageUrl) {
                    onOrder({ product: effectiveProduct, label, imageUrl });
                }
            } catch (err) {
                console.error('Quick order failed:', err);
            } finally {
                setActiveProductId(null);
                setProgress(0);
            }
        },
        [activeProductId, effectiveCaption, noLabelFlags, prepareImage, onOrder]
    );

    return (
        <Box
            sx={{
                mt: { xs: 1.5, sm: 2 },
                mb: { xs: 2, sm: 2.5 },
                p: { xs: 1.5, sm: 2 },
                backgroundColor: PALETTE.primary50,
                border: `1px solid ${alpha('primary', 0.18)}`,
                borderRadius: 3,
            }}
        >
            <Typography
                component="h3"
                sx={{
                    textAlign: 'center',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    fontWeight: 700,
                    color: PALETTE.text,
                    mb: 0.25,
                }}
            >
                ✨ Make it yours — order on a product
            </Typography>
            <Typography
                variant="caption"
                sx={{
                    display: 'block',
                    textAlign: 'center',
                    color: PALETTE.textMuted,
                    mb: { xs: 1.25, sm: 1.5 },
                }}
            >
                Your design ships on a cup, t-shirt, magnet & more.
            </Typography>

            <PromoCodeBar />

            {expanded ? (
                <ProductGallery
                    defaultLabel={effectiveCaption}
                    prepareImage={prepareImage}
                    onOrder={onOrder}
                    hideHeader
                    hidePromo
                    designSrc={designSrc}
                />
            ) : (
                <>
                    <Box sx={{ mb: { xs: 1.25, sm: 1.5 } }}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Caption (printed on product)"
                            value={effectiveCaption}
                            onChange={(e) => setCaptionOverride(e.target.value)}
                            placeholder="e.g. 27 states visited"
                            sx={{
                                backgroundColor: '#ffffff',
                                borderRadius: 1,
                                '& .MuiFormHelperText-root': {
                                    color: PALETTE.textMuted,
                                    mx: 0.5,
                                    mt: 0.5,
                                },
                            }}
                        />
                    </Box>
                    <Typography
                        sx={{
                            textAlign: 'center',
                            color: PALETTE.text,
                            fontSize: { xs: '0.85rem', sm: '0.95rem' },
                            fontWeight: 600,
                            mb: { xs: 1, sm: 1.25 },
                            px: 1,
                        }}
                    >
                        Approximate preview — you'll see the exact one
                        on the manufacturer's site before checkout.
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: 'repeat(2, minmax(0, 1fr))',                                       // 2 cols on mobile
                                sm: `repeat(${Math.min(featuredProducts.length, 3)}, minmax(0, 1fr))`, // 3 cols on tablet
                                md: `repeat(${featuredProducts.length}, minmax(0, 1fr))`,              // full row on desktop+
                            },
                            gap: { xs: 1, sm: 1.5 },
                        }}
                    >
                        {featuredProducts.map((product) => {
                            const isLoading = activeProductId === product.id;
                            const anyLoading = !!activeProductId;
                            const noLabelChecked = !!noLabelFlags[product.id];
                            const canToggleNoLabel = product.supportsLabel;
                            const { livePreviewCaption, liveBounds } = resolvePreviewProps(
                                product,
                                { designSrc, caption: effectiveCaption, noLabel: noLabelChecked },
                            );
                            return (
                                <Box
                                    key={product.id}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        backgroundColor: '#ffffff',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        boxShadow: `0 1px 2px ${alpha('text', 0.04)}`,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            width: '100%',
                                            aspectRatio: '1 / 1',
                                            backgroundColor: '#f1f5f9',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: PALETTE.textFaint,
                                        }}
                                    >
                                        <ProductPreviewArea
                                            product={product}
                                            designSrc={designSrc}
                                            livePreviewCaption={livePreviewCaption}
                                            liveBounds={liveBounds}
                                            displayedImage={noLabelChecked && product.noLabelImage ? product.noLabelImage : product.image}
                                        />
                                    </Box>

                                    <Box
                                        sx={{
                                            p: { xs: 0.75, sm: 1 },
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.5,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                color: PALETTE.text,
                                                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                                textAlign: 'center',
                                            }}
                                        >
                                            {product.name}
                                        </Typography>

                                        {canToggleNoLabel && (
                                            <FormControlLabel
                                                sx={{ m: 0, justifyContent: 'center' }}
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        sx={{ py: 0 }}
                                                        checked={noLabelChecked}
                                                        onChange={(e) => handleNoLabelToggle(product.id, e.target.checked)}
                                                        disabled={isLoading}
                                                    />
                                                }
                                                label={<Typography variant="caption" sx={{ color: PALETTE.textBody }}>No caption</Typography>}
                                            />
                                        )}

                                        {isLoading && (
                                            <Box>
                                                <LinearProgress
                                                    variant={progress > 0 ? 'determinate' : 'indeterminate'}
                                                    value={progress}
                                                />
                                            </Box>
                                        )}

                                        <ActionButton
                                            variant="primary"
                                            startIcon={<ShoppingCart sx={{ fontSize: 16 }} />}
                                            onClick={() => handleOrderClick(product)}
                                            disabled={anyLoading && !isLoading}
                                            loading={isLoading}
                                            sx={{
                                                width: '100%',
                                                minWidth: 0,
                                                height: { xs: 32, sm: 36 },
                                                fontSize: { xs: '0.78rem', sm: '0.85rem' },
                                                px: 0.5,
                                            }}
                                        >
                                            Order
                                        </ActionButton>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </>
            )}

            <Box sx={{ textAlign: 'center', mt: { xs: 1, sm: 1.25 } }}>
                <Box
                    component="button"
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    sx={{
                        border: 'none',
                        background: 'transparent',
                        color: PALETTE.primary,
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        padding: '4px 8px',
                        borderRadius: 1,
                        '&:hover': {
                            textDecoration: 'underline',
                            backgroundColor: alpha('primary', 0.06),
                        },
                    }}
                >
                    {expanded ? (
                        <>
                            Show less
                            <ArrowUpward sx={{ fontSize: 14 }} />
                        </>
                    ) : (
                        <>
                            See all {VISIBLE_PRODUCT_COUNT} products
                            <ArrowDownward sx={{ fontSize: 14 }} />
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

QuickOrderStrip.propTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    featuredProducts: PropTypes.array,
    defaultLabel: PropTypes.string,
    prepareImage: PropTypes.func.isRequired,
    onOrder: PropTypes.func.isRequired,
    designSrc: PropTypes.string,
};

export default QuickOrderStrip;
